// shell/components/entity-fields/view-renderers/files-renderer.ts

import { createElement } from '../../../utils/dom';
import { formatFileSize } from '../../../utils/format';
import type { FilesViewField, FileInfo } from '../../entity-view/types';

/**
 * Renders a list of files with download capability.
 */
export function renderFilesView<T>(
    field: FilesViewField<T>,
    value: unknown,
    _model: T
): HTMLElement {
    const container = createElement('div', {
        className: 'ev-field-view ev-field-view--files',
    });

    if (value === null || value === undefined) {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    // Handle array of files
    const files: FileInfo[] = Array.isArray(value) ? value : [value as FileInfo];

    if (files.length === 0) {
        container.classList.add('ev-field-view--empty');
        container.textContent = '—';
        return container;
    }

    const fileList = createElement('div', {
        className: 'ev-file-list',
    });

    for (const file of files) {
        const fileItem = renderFileItem(file, field);
        fileList.appendChild(fileItem);
    }

    container.appendChild(fileList);
    return container;
}

function renderFileItem<T>(file: FileInfo, field: FilesViewField<T>): HTMLElement {
    const item = createElement('div', {
        className: 'ev-file-item',
    });

    // File icon
    const icon = createElement('span', {
        className: `ev-file-icon ${getFileIconClass(file.mimeType)}`,
        attributes: { 'aria-hidden': 'true' },
    });
    item.appendChild(icon);

    // File info container
    const info = createElement('div', {
        className: 'ev-file-info',
    });

    // File name (as link if URL available)
    if (file.url || field.onDownload) {
        const link = createElement('a', {
            className: 'ev-file-name',
            textContent: file.name,
            attributes: {
                href: file.url ?? '#',
                ...(file.url ? { download: file.name } : {}),
            },
            onClick: async (e) => {
                if (field.onDownload) {
                    e.preventDefault();
                    await field.onDownload(file);
                }
            },
        });
        info.appendChild(link);
    } else {
        const name = createElement('span', {
            className: 'ev-file-name',
            textContent: file.name,
        });
        info.appendChild(name);
    }

    // File size
    const size = createElement('span', {
        className: 'ev-file-size',
        textContent: formatFileSize(file.size),
    });
    info.appendChild(size);

    item.appendChild(info);

    // Preview thumbnail if enabled and is image
    if (field.showPreview && file.url && isImageMimeType(file.mimeType)) {
        const preview = createElement('img', {
            className: 'ev-file-preview',
            attributes: {
                src: file.url,
                alt: file.name,
            },
        });
        item.appendChild(preview);
    }

    return item;
}

function getFileIconClass(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'ev-file-icon--image';
    if (mimeType.startsWith('video/')) return 'ev-file-icon--video';
    if (mimeType.startsWith('audio/')) return 'ev-file-icon--audio';
    if (mimeType === 'application/pdf') return 'ev-file-icon--pdf';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'ev-file-icon--spreadsheet';
    if (mimeType.includes('document') || mimeType.includes('word')) return 'ev-file-icon--document';
    if (mimeType.includes('zip') || mimeType.includes('compressed')) return 'ev-file-icon--archive';
    return 'ev-file-icon--generic';
}

function isImageMimeType(mimeType: string): boolean {
    return mimeType.startsWith('image/');
}

